<template>
    <div class="asset-trend">
        <div class="asset-trend__head">
            <h6 class="mb-0 mr-3">자산현황</h6>
            <small class="text-muted">현금 · 재고자산 · 정산대기 일자별 누적</small>

            <div class="flex-grow-1"></div>

            <b-button-group size="sm" class="mr-2">
                <b-button
                    :variant="baseMode === 'start' ? 'primary' : 'outline-secondary'"
                    title="그때 통장에 얼마 있었는지 알 때. 이후로 얼마나 불었는지 보입니다"
                    @click="setBaseMode('start')"
                >시작일 기준</b-button>
                <b-button
                    :variant="baseMode === 'today' ? 'primary' : 'outline-secondary'"
                    title="시작 잔고가 기억나지 않을 때. 오늘 잔고에서 거꾸로 계산합니다"
                    @click="setBaseMode('today')"
                >오늘 기준</b-button>
            </b-button-group>

            <b-form-input v-model="baseDate" type="date" size="sm" class="mr-2" style="width: 145px;" />
            <span class="mr-2 small text-muted">그날 통장 잔고</span>
            <b-form-input
                v-model.number="baseAmount"
                type="number"
                size="sm"
                class="mr-2 text-right"
                style="width: 150px;"
                placeholder="0"
            />
            <b-button size="sm" variant="primary" @click="emitSetting">저장</b-button>
        </div>

        <div class="asset-trend__body">
            <div v-if="inboundFrom" class="alert alert-light py-2 mb-3 small text-muted">
                재고 변화는 입고 기록이 있는 <strong>{{ inboundFrom }}</strong> 이후 구간만 계산했어요.
                그 이전은 같은 값으로 두었습니다.
            </div>

            <div v-if="!hasSetting" class="alert alert-info py-2 mb-3 small">
                통장 잔고를 한 번 알려주시면 현금이 어떻게 움직였는지 그려져요. 두 가지 방법이 있어요.
                <div class="mt-1">
                    · <strong>시작일 기준</strong> — 그때 얼마로 시작했는지 안다면.
                    거기서 지금까지 <strong>얼마나 불었는지</strong>가 보여요.<br>
                    · <strong>오늘 기준</strong> — 시작 잔고가 기억나지 않는다면.
                    지금 잔고를 넣으면 지난 기록으로 <strong>거꾸로 계산</strong>해 드려요.
                </div>
            </div>
            <div v-else class="alert alert-light py-2 mb-3 small text-muted">
                <template v-if="baseMode === 'today'">
                    <strong>{{ setting.baseCashDate }}</strong> 잔고 {{ fmt(setting.baseCashAmount) }}원에서
                    지난 기록을 거꾸로 되짚어 그린 그래프예요. 최근 값이 정확하고,
                    과거로 갈수록 기록에 없는 입출금만큼 어긋날 수 있어요.
                </template>
                <template v-else>
                    <strong>{{ setting.baseCashDate }}</strong> 잔고 {{ fmt(setting.baseCashAmount) }}원을 시작점으로,
                    판매 · 매입 · 비용 기록을 하루씩 더해 그린 그래프예요.
                    <span v-if="cashEndVsStart !== null">
                        그 뒤로
                        <strong :class="cashEndVsStart >= 0 ? 'text-success' : 'text-danger'">
                            {{ cashEndVsStart >= 0 ? '+' : '' }}{{ fmt(cashEndVsStart) }}원
                        </strong>
                        바뀌었습니다.
                    </span>
                    실제 잔고와 차이가 나면 대출·세금처럼 쿠플러스가 모르는 입출금이 있다는 뜻이에요.
                </template>
            </div>

            <div v-if="!series.length" class="text-center text-muted py-5">
                조회된 기간에 데이터가 없습니다.
            </div>

            <template v-else>
                <!-- 요약 카드 -->
                <div class="row text-center mb-3">
                    <div class="col">
                        <div class="asset-trend__kpi">
                            <span>총자산</span>
                            <strong class="text-primary">{{ fmt(latest.total) }}원</strong>
                        </div>
                    </div>
                    <div class="col">
                        <div class="asset-trend__kpi">
                            <span>현금</span>
                            <strong :class="latest.cash >= 0 ? 'text-success' : 'text-danger'">{{ fmt(latest.cash) }}원</strong>
                        </div>
                    </div>
                    <div class="col">
                        <div class="asset-trend__kpi">
                            <span>재고자산</span>
                            <strong style="color:#e0912c">{{ fmt(latest.stock) }}원</strong>
                        </div>
                    </div>
                    <div class="col">
                        <div class="asset-trend__kpi asset-trend__kpi--pop">
                            <span>받을 돈 <i class="fas fa-calendar-alt ml-1" style="font-size:11px;opacity:.5"></i></span>
                            <strong style="color:#2196c4">{{ fmt(latest.receivable) }}원</strong>

                            <!-- 언제 얼마가 들어올지 -->
                            <div class="asset-trend__schedule">
                                <div class="asset-trend__schedule-head">입금 예정</div>
                                <div v-if="!payoutSchedule.length" class="text-muted small py-1">
                                    예정된 입금이 없어요.
                                </div>
                                <div v-for="p in payoutSchedule" :key="p.date" class="asset-trend__schedule-row">
                                    <span>
                                        {{ p.date.slice(5) }}
                                        <small class="text-muted">{{ p.dayLabel }}</small>
                                    </span>
                                    <b>
                                        {{ fmt(p.amount) }}원
                                        <small v-if="compensationOn(p.date)" class="d-block text-muted font-weight-normal">
                                            재고손실보상 {{ fmt(compensationOn(p.date)) }}원 포함
                                        </small>
                                    </b>
                                </div>
                                <div v-if="payoutSchedule.length" class="asset-trend__schedule-row is-total">
                                    <span>합계</span>
                                    <b>{{ fmt(payoutTotal) }}원</b>
                                </div>
                                <small class="text-muted d-block mt-1">
                                    <template v-if="hasRealSchedule">
                                        날짜는 쿠팡이 알려준 정산일이고, 금액은 아직 정산 전이라 예상입니다.
                                    </template>
                                    <template v-else>
                                        정산주기로 계산한 예상 날짜·금액이에요.
                                    </template>
                                </small>
                            </div>
                        </div>
                    </div>
                    <!-- 확인 안 된 재고 — 창고에서 확인 안 되는 재고 × 매입원가(공급관리 요약칸과 같은 이름). 누르면 공급관리(SCM) 미확인 목록 -->
                    <div v-if="recon" class="col">
                        <div
                            class="asset-trend__kpi asset-trend__kpi--link"
                            role="button"
                            tabindex="0"
                            :title="reconTitle"
                            @click="$emit('open-recon')"
                            @keydown.enter="$emit('open-recon')"
                        >
                            <span>확인 안 된 재고</span>
                            <template v-if="reconExact.count > 0">
                                <strong class="asset-trend__kpi-recon">{{ fmt(reconExact.amount) }}원</strong>
                                <small class="text-muted">{{ fmt(reconExact.miss) }}개 × 매입원가</small>
                            </template>
                            <strong v-else class="text-muted">계산 준비 중</strong>
                        </div>
                    </div>
                </div>

                <!-- 진단: 지금 무엇을 해야 하나 -->
                <div v-if="diagnosis" class="asset-trend__diag" :class="'is-' + diagnosis.level">
                    <div class="asset-trend__diag-title">{{ diagnosis.title }}</div>
                    <div class="asset-trend__diag-action">{{ diagnosis.action }}</div>
                    <div class="asset-trend__diag-why">{{ diagnosis.why }}</div>
                    <div class="asset-trend__diag-metrics">
                        <span title="지금 재고를 다 파는 데 걸리는 기간">
                            재고 <strong>{{ Math.round(stockDays) }}일치</strong>
                        </span>
                        <span title="판매는 됐지만 아직 입금되지 않은 금액을 일수로 환산한 값">
                            받을 돈 <strong>{{ Math.round(receivableDays) }}일치</strong>
                        </span>
                        <span title="지금 속도로 돈이 나가면 현금이 버티는 기간">
                            현금
                            <strong v-if="runwayDays === null" class="text-success">늘고 있어요</strong>
                            <strong v-else>{{ Math.round(runwayDays) }}일치</strong>
                        </span>
                        <span v-if="salesTrend !== null" title="최근 30일 일평균 매출을 그 앞 30일과 비교한 값">
                            매출
                            <strong :class="salesTrendClass">{{ salesTrendLabel }}</strong>
                        </span>
                    </div>
                </div>

                <!-- 누적 영역 차트 -->
                <div class="asset-trend__chart">
                    <svg :viewBox="'0 0 ' + W + ' ' + H" preserveAspectRatio="none" class="asset-trend__svg">
                        <!-- 가로 눈금 -->
                        <g>
                            <line
                                v-for="(g, i) in gridLines"
                                :key="'g' + i"
                                :x1="PAD_L" :x2="W - PAD_R"
                                :y1="g.y" :y2="g.y"
                                stroke="#eef1f4" stroke-width="1"
                            />
                        </g>
                        <!-- 0선 -->
                        <line :x1="PAD_L" :x2="W - PAD_R" :y1="yOf(0)" :y2="yOf(0)" stroke="#cfd8e0" stroke-width="1" />
                        <!-- 유동성이 높은 것부터 아래에 쌓는다: 현금 → 받을 돈 → 재고.
                             현금을 맨 위에 두면 현금의 윗선이 곧 총자산선이 되어
                             '현금이 느는 것'과 '자산이 느는 것'이 구분되지 않는다.
                             경로는 computed 라 마우스를 움직여도 다시 계산되지 않는다 -->
                        <path :d="areaCash" fill="#5cb85c" fill-opacity="0.30" />
                        <path :d="areaReceivable" fill="#5bc0de" fill-opacity="0.32" />
                        <path :d="areaStock" fill="#f0ad4e" fill-opacity="0.32" />

                        <!-- 각 구간의 경계선을 진하게 — 면 색만으로는 구분이 잘 안 된다 -->
                        <path :d="lineCash" fill="none" stroke="#2f9e4f" stroke-width="2" />
                        <path :d="lineCashPlusRecv" fill="none" stroke="#2196c4" stroke-width="2" />
                        <!-- 총자산 실선 -->
                        <path :d="lineTotal" fill="none" stroke="#2b6cb0" stroke-width="2.5" />

                        <!-- 마우스 올린 날 표시 -->
                        <g v-if="hover !== null">
                            <line
                                :x1="xOf(hover)" :x2="xOf(hover)" :y1="PAD_T" :y2="H - PAD_B"
                                stroke="#2b6cb0" stroke-width="1" stroke-dasharray="3 3"
                            />
                            <circle :cx="xOf(hover)" :cy="yOf(series[hover].total)" r="3.5" fill="#2b6cb0" />
                        </g>

                        <!-- 마우스 감지 영역 (날짜가 많아 촘촘하므로 전체를 덮고 좌표로 환산) -->
                        <rect
                            :x="PAD_L" :y="0" :width="W - PAD_L - PAD_R" :height="H"
                            fill="transparent"
                            @mousemove="onMove"
                            @mouseleave="hover = null"
                        />
                    </svg>

                    <div v-if="hover !== null" class="asset-trend__tip" :style="tipStyle">
                        <div class="asset-trend__tip-date">{{ series[hover].date }}</div>
                        <div class="asset-trend__tip-row">
                            <span><i class="dot" style="background:#2f9e4f"></i>현금</span>
                            <b :class="{ 'text-danger': series[hover].cash < 0 }">{{ fmt(series[hover].cash) }}원</b>
                        </div>
                        <div class="asset-trend__tip-row">
                            <span><i class="dot" style="background:#2196c4"></i>받을 돈</span>
                            <b>{{ fmt(series[hover].receivable) }}원</b>
                        </div>
                        <div class="asset-trend__tip-row">
                            <span><i class="dot" style="background:#e0912c"></i>재고자산</span>
                            <b>{{ fmt(series[hover].stock) }}원</b>
                        </div>
                        <div class="asset-trend__tip-row is-total">
                            <span>총자산</span>
                            <b>{{ fmt(series[hover].total) }}원</b>
                        </div>
                    </div>

                    <div class="asset-trend__axis">
                        <span v-for="(t, i) in xTicks" :key="'x' + i" :style="{ left: t.pct + '%' }">{{ t.label }}</span>
                    </div>
                </div>

                <div class="asset-trend__legend">
                    <small class="text-muted mr-1">아래부터</small>
                    <span><i class="dot" style="background:#2f9e4f"></i>현금</span>
                    <span><i class="dot" style="background:#2196c4"></i>받을 돈</span>
                    <span><i class="dot" style="background:#e0912c"></i>재고자산</span>
                    <span><i class="dot line" style="background:#2b6cb0"></i>총자산</span>
                </div>

                <div class="asset-trend__note">
                    <template v-if="hasRealSchedule">
                        입금 시점은 쿠팡이 알려준 실제 정산일{{ settlementCycle === 'MONTHLY' ? '(월정산)' : '' }} 기준이에요.
                    </template>
                    <template v-else>
                        입금 시점은 정산주기로 추정한 값이에요. 수집을 한 번 더 돌리면 실제 정산일로 바뀝니다.
                    </template>
                    <strong>재고자산</strong>은 창고에 있는 상품을 매입가로 환산한 금액이에요.
                    <strong>정산대기</strong>는 이미 팔렸지만 아직 통장에 안 들어온 돈이고,
                    <strong>현금</strong>은 입력하신 잔고를 기준으로 정산입금·매입·비용을 더하고 뺀 값이에요.
                    대출이나 세금 납부처럼 쿠플러스가 모르는 입출금은 빠져 있어요.
                </div>
            </template>
        </div>
    </div>
</template>

<script>
/**
 * 자산현황 — 현금 / 재고자산 / 정산대기 일자별 누적 영역 차트
 *
 * 세 값 모두 '기준점에서 앞뒤로 감는' 방식이다.
 *   재고자산 : 상품별 재고 스냅샷(InventoryCoverage.computedAt)이 기준점.
 *              스냅샷 시점이 상품마다 다르므로 상품별로 각자 감는다.
 *   현금     : 사용자가 입력한 기준일 잔고가 기준점.
 *   정산대기 : 판매 시점에 쌓이고 정산 지급일에 빠지는 큐.
 *
 * 근거·한계는 docs/sales-ledger-pnl-design.md 참고.
 */
import { buildAssetSeries } from '@/utils/assetSeries';

export default {
    name: 'AssetTrendChart',
    props: {
        dailyData: { type: Array, required: true },
        fixedCosts: { type: Array, default: () => [] },
        productCosts: { type: Array, default: () => [] },
        // { baseCashDate, baseCashAmount }
        setting: { type: Object, default: null },
        // 상품별 재고 스냅샷 [{ vendorItemId, totalStock, totalStockAmount, purchasePrice, computedAt }]
        coverage: { type: Array, default: () => [] },
        // 입고이력 [{ stowedDate, skus:[{vendorItemId, stowedQty}] }]
        inbound: { type: Array, default: () => [] },
        // 실제 정산 일정 [{ periodStart, periodEnd, settlementDate, cycle }] — 쿠팡이 정한 입금일
        schedules: { type: Array, default: () => [] },
        // 정산주기(종료일)가 없는 과거 데이터용 — 매출인식일 + 이 일수로 근사
        settlementLagDays: { type: Number, default: 15 },
        // 정산주기 마감일로부터 실제 지급까지 걸리는 일수
        payoutLagDays: { type: Number, default: 15 },
        // 상품별 리드타임을 모를 때 쓸 기본값 (발주 → 입고까지 걸리는 일수)
        defaultLeadDays: { type: Number, default: 20 },
        // 상품별 매입 lot [{ vendorItemId, 매입정보:[{date,매입가,수량,입고ID}] }]
        // 입고 하나가 곧 lot 이라 입고ID(=shipmentId)로 정확히 붙는다 — FIFO 재구성이 필요 없다.
        // 비어 있으면 지금까지 쓰던 coverage.purchasePrice 대표값으로 돌아간다(정본 §4 안전 원칙).
        purchaseLots: { type: Array, default: () => [] },
        // 재고 대사 요약(/api/growth/getInventoryRecon summary) — 「확인 안 된 재고」 칸. 없으면 칸을 안 그린다
        // { exact: { count, miss, amount, ... }, compensationPendingByDate } — 화면은 exact(정확 모드 합계)만 쓴다
        recon: { type: Object, default: null }
    },
    data() {
        return {
            baseDate: '',
            baseAmount: 0,
            hover: null,
            moveRaf: null,
            W: 1000,
            H: 320,
            PAD_L: 8,
            PAD_R: 8,
            PAD_T: 12,
            PAD_B: 12
        };
    },
    computed: {
        hasSetting() {
            return !!(this.setting && this.setting.baseCashDate);
        },
        // 날짜·흐름·재고·현금·정산대기 계산은 utils/assetSeries.js 한 곳에 둔다.
        // 대시보드 「현금자산·재고자산」 타일이 같은 함수를 불러, 두 화면 숫자가 갈리지 않는다.
        model() {
            return buildAssetSeries({
                dailyData: this.dailyData,
                fixedCosts: this.fixedCosts,
                productCosts: this.productCosts,
                setting: this.setting,
                coverage: this.coverage,
                inbound: this.inbound,
                schedules: this.schedules,
                purchaseLots: this.purchaseLots,
                payoutLagDays: this.payoutLagDays,
                defaultLeadDays: this.defaultLeadDays
            });
        },
        dates() { return this.model.dates; },
        flows() { return this.model.flows; },
        inboundLots() { return this.model.inboundLots; },

        // 창고에 들어온 날 기준 금액 (재고 역산에 쓴다)
        inboundAmountByDate() {
            const map = {};
            this.inboundLots.forEach(l => {
                map[l.arriveDate] = (map[l.arriveDate] || 0) + l.amount;
            });
            return map;
        },

        purchasePayByDate() { return this.model.purchasePayByDate; },
        inboundFrom() { return this.model.inboundFrom; },
        stockSeries() { return this.model.stockSeries; },

        // ===== 자본 회전 진단 =====
        // 금액 비중이 아니라 회전일수로 본다. 매출 규모가 다르면 같은 금액이
        // 여유일 수도 부족일 수도 있어서, 비중(%)으로 판단하면 처방이 뒤집힌다
        periodDays() {
            return Math.max(1, this.dates.length);
        },
        // 하루 평균 매입원가 = 재고가 빠져나가는 속도.
        //
        // 재고금액과 같은 출처(SCM이 계산해 둔 InventoryCoverage)를 쓴다.
        // 다만 상품별 stockDays 를 평균 내지는 않는다 — 재고는 있는데 안 팔리는 상품이
        // stockDays 0 으로 저장돼 있어(실제로는 무한대) 평균이 실제보다 짧게 나온다.
        // 대신 전체를 한 번에 나눠 그 재고까지 분모 없이 분자에만 반영되게 한다.
        //
        // SCM 데이터가 없으면 실제 판매 흐름(최근 30일)으로 대신한다.
        dailyCogs() {
            const fromScm = (this.coverage || []).reduce(
                (s, c) => s + this.num(c.dailySales) * this.num(c.purchasePrice), 0);
            if (fromScm > 0) return fromScm;

            const dates = this.dates;
            if (!dates.length) return 0;
            const window = Math.min(30, dates.length);
            let sum = 0;
            for (let i = dates.length - window; i < dates.length; i++) {
                sum += this.num((this.flows[dates[i]] || {}).purchase);
            }
            return sum / window;
        },

        // 안 팔리는 재고가 많으면 그것부터 짚어준다
        deadStockHint() {
            const share = this.deadStockShare;
            if (share < 0.2) return '';
            const amount = (this.coverage || []).reduce(
                (s, c) => s + (this.num(c.dailySales) <= 0 ? this.num(c.totalStockAmount) : 0), 0);
            return ` 특히 최근 판매가 없는 재고가 ${this.fmt(amount)}원(${Math.round(share * 100)}%)이라 이쪽부터 보시면 됩니다.`;
        },

        // 재고는 있는데 안 팔리는 상품의 금액 비중 (진단 근거로 쓴다)
        deadStockShare() {
            let dead = 0;
            let total = 0;
            (this.coverage || []).forEach(c => {
                const amt = this.num(c.totalStockAmount);
                if (amt <= 0) return;
                total += amt;
                if (this.num(c.dailySales) <= 0) dead += amt;
            });
            return total > 0 ? dead / total : 0;
        },
        // 하루 평균 정산 수령액
        // 하루 평균 정산 대상액 — '받을 돈이 며칠치인가'의 분모.
        //
        // 실제 입금액이 아니라 판매로 쌓이는 속도(기여이익)를 쓴다. 월정산이면 최근 30일에
        // 입금 이벤트가 0~1건뿐이라 입금액 기준으로는 값이 널뛴다
        dailyPayout() {
            const dates = this.dates;
            if (!dates.length) return 0;
            const window = Math.min(30, dates.length);
            let sum = 0;
            for (let i = dates.length - window; i < dates.length; i++) {
                sum += this.num((this.flows[dates[i]] || {}).gp);
            }
            return sum / window;
        },
        // 지금 재고가 며칠치인가
        stockDays() {
            return this.dailyCogs > 0 ? this.latest.stock / this.dailyCogs : 0;
        },
        // 정산받을 돈이 며칠 묶여 있나
        receivableDays() {
            return this.dailyPayout > 0 ? this.latest.receivable / this.dailyPayout : 0;
        },
        // 매출이 늘고 있나 — 최근 30일 일평균 vs 그 앞 30일 일평균
        salesTrend() {
            const dates = this.dates;
            if (dates.length < 45) return null;
            const avg = (from, to) => {
                let sum = 0;
                for (let i = from; i < to; i++) sum += this.num((this.flows[dates[i]] || {}).netSales);
                return sum / Math.max(1, to - from);
            };
            const n = dates.length;
            const recent = avg(Math.max(0, n - 30), n);
            const before = avg(Math.max(0, n - 60), Math.max(0, n - 30));
            if (!before) return null;
            return (recent - before) / before;   // +0.1 = 10% 늘었다
        },

        salesTrendLabel() {
            if (this.salesTrend === null) return '';
            const arrow = this.salesTrend > 0.05 ? '↑' : this.salesTrend < -0.05 ? '↓' : '→';
            return arrow + ' ' + (this.salesTrend * 100).toFixed(0) + '%';
        },
        salesTrendClass() {
            if (this.salesTrend === null) return '';
            if (this.salesTrend > 0.05) return 'text-success';
            if (this.salesTrend < -0.05) return 'text-danger';
            return '';
        },

        // 최근 30일 현금 기울기 (하루 평균 증감)
        cashSlope() {
            const n = this.series.length;
            if (n < 8) return 0;
            const span = Math.min(30, n - 1);
            const from = this.series[n - 1 - span].cash;
            const to = this.series[n - 1].cash;
            return (to - from) / span;
        },
        // 현금이 바닥날 때까지 며칠. 늘고 있으면 null
        runwayDays() {
            if (this.cashSlope >= 0) return null;
            if (this.latest.cash <= 0) return 0;
            return this.latest.cash / Math.abs(this.cashSlope);
        },

        diagnosis() {
            if (!this.series.length || this.dailyCogs <= 0) return null;

            const stock = Math.round(this.stockDays);
            const runway = this.runwayDays === null ? null : Math.round(this.runwayDays);
            const recv = Math.round(this.receivableDays);
            const cash = this.latest.cash;

            // 위에서부터 먼저 걸리는 하나만 보여준다. 여러 개 늘어놓으면 안 읽힌다
            if (cash < 0) {
                const canCover = this.latest.receivable > Math.abs(cash);
                return {
                    level: 'danger',
                    title: '나갈 돈이 가진 돈보다 많아요',
                    action: canCover
                        ? `다행히 곧 들어올 정산금 ${this.fmt(this.latest.receivable)}원이 부족한 만큼보다 커요. ` +
                          '빠른정산을 신청하거나 다음 발주를 며칠 미루면 넘길 수 있어요.'
                        : '당분간 발주를 멈추거나, 마이너스 통장·대출 같은 외부 자금을 미리 준비해 두는 게 좋아요.',
                    why: `지금 부족한 금액은 ${this.fmt(Math.abs(cash))}원이고, 정산 예정 금액은 ${this.fmt(this.latest.receivable)}원이에요.`
                };
            }
            if (runway !== null && runway < 14) {
                const tight = recv > 20;
                return {
                    level: 'danger',
                    title: `이대로 가면 ${runway}일 뒤에 현금이 바닥나요`,
                    action: tight
                        ? '상품은 잘 팔리는데 정산금이 아직 안 들어와서 생긴 일이에요. 물건 자체는 문제없으니 ' +
                          '다음 발주만 잠시 미루거나, 빠른정산으로 돈이 들어오는 시점을 앞당겨 보세요.'
                        : '지금은 새로 사들이는 양을 줄여서 현금을 아껴 두는 게 좋아요.',
                    why: `하루 평균 ${this.fmt(Math.abs(this.cashSlope))}원씩 줄고 있어요. ` +
                         `정산받을 돈은 ${recv}일치가 밀려 있어요.`
                };
            }
            // 재고도 쌓이고 현금도 쌓이는 상태.
            // 하나만 짚으면 "재고 줄여라"로 끝나는데, 진짜 문제는 '지금 상품군이 흡수할 수 있는
            // 양을 넘었다'는 것이다. 같은 상품을 더 발주하면 재고만 늘고, 현금은 계속 놀게 된다
            if (stock > 60 && runway === null) {
                const flat = this.salesTrend !== null && Math.abs(this.salesTrend) < 0.1;
                return {
                    level: 'info',
                    title: '판매활동과 소싱을 둘 다 늘리세요',
                    action: '재고가 많으니 광고 · 상위노출 · 쿠폰으로 판매를 늘리시고, ' +
                            '현금도 남으니 소싱도 같이 늘리시면 됩니다.' + this.deadStockHint,
                    why: `재고 ${stock}일치 · 현금 ${this.fmt(cash)}원` +
                         (flat ? ' · 매출은 제자리' : ` · 매출 ${this.salesTrend > 0 ? '상승' : '하락'} 중`)
                };
            }
            if (stock > 60) {
                return {
                    level: 'warn',
                    title: '판매활동을 더 적극적으로 하세요',
                    action: '광고 · 상위노출 · 쿠폰으로 파는 속도를 올리세요. 오래 묶일수록 보관비도 붙습니다.' + this.deadStockHint,
                    why: `재고 ${stock}일치 (${this.fmt(this.latest.stock)}원) · 하루 ${this.fmt(this.dailyCogs)}원어치씩 판매`
                };
            }
            if (stock < 21 && (runway === null || runway > 45)) {
                return {
                    level: 'info',
                    title: '소싱 속도를 올리세요',
                    action: '현금에 여유가 있습니다. 발주량을 늘리거나 새 상품을 들이세요. ' +
                            '들여올 상품이 없어서 못 늘리는 거라면 병목은 돈이 아니라 소싱입니다.',
                    why: `재고 ${stock}일치 · 현금 ${this.fmt(cash)}원`
                };
            }
            if (stock < 14) {
                return {
                    level: 'warn',
                    title: '지금 발주하세요',
                    action: '입고까지 걸리는 시간을 감안하면 지금 넣어야 합니다. 품절되면 검색 순위도 같이 떨어집니다.',
                    why: `재고 ${stock}일치 · 하루 ${this.fmt(this.dailyCogs)}원어치씩 판매`
                };
            }
            return {
                level: 'ok',
                title: '지금 흐름이 좋아요',
                action: '재고도 현금도 적당한 상태예요. 지금 속도를 유지하시면 돼요.',
                why: `재고 ${stock}일치 · 정산받을 돈 ${recv}일치` +
                     (runway === null ? ' · 현금이 늘고 있어요' : ` · 현금은 ${runway}일치 남아 있어요`)
            };
        },

        sortedSchedules() { return this.model.sortedSchedules; },
        settlementCycle() { return this.model.settlementCycle; },

        vatPayByDate() { return this.model.vatPayByDate; },

        incomeByDate() { return this.model.incomeByDate; },

        // 쿠팡이 알려준 실제 정산 일정을 쓰고 있는지 (아니면 주기로 추정 중인지)
        hasRealSchedule() {
            return this.sortedSchedules.length > 0;
        },

        // 아직 안 들어온 입금 예정 (마지막 날 이후에 도착할 것들)
        payoutSchedule() {
            if (!this.dates.length) return [];
            const last = this.dates[this.dates.length - 1];
            return Object.keys(this.incomeByDate)
                .filter(d => d > last)
                .sort()
                .map(d => ({
                    date: d,
                    amount: this.num(this.incomeByDate[d]),
                    dayLabel: this.dayLabelOf(d)
                }))
                .filter(p => p.amount !== 0)
                .slice(0, 12);
        },
        payoutTotal() {
            return this.payoutSchedule.reduce((s, p) => s + p.amount, 0);
        },

        // 「확인 안 된 재고」 — 서버가 정확 모드 옵션만 합산한 summary.exact. 범위는 보여주지 않는다
        reconExact() {
            const e = (this.recon && this.recon.exact) || {};
            return { count: this.num(e.count), miss: this.num(e.miss), amount: Math.round(this.num(e.amount)) };
        },
        reconTitle() {
            if (this.reconExact.count === 0) return '쿠팡 재고 이동 기록이 수집되면 자동으로 계산됩니다';
            return '입고·판매·반품·현재고를 맞춰 봤을 때 창고에서 확인되지 않는 재고를 매입원가로 환산한 금액이에요. 정확히 계산된 상품만 더했어요. 누르면 공급관리에서 해당 상품을 봅니다. 총자산에는 더하지 않았어요.';
        },

        // 정산대기·현금 시계열 — 계산은 utils/assetSeries.js (대시보드 자산 타일과 공용)
        receivableSeries() { return this.model.receivableSeries; },
        cashSeries() { return this.model.cashSeries; },

        series() { return this.model.series; },
        latest() {
            return this.series.length ? this.series[this.series.length - 1] : { cash: 0, stock: 0, receivable: 0, total: 0 };
        },
        maxValue() {
            let max = 0;
            this.series.forEach(s => { if (s.total > max) max = s.total; });
            return max || 1;
        },
        minValue() {
            let min = 0;
            this.series.forEach(s => {
                const bottom = Math.min(0, s.cash, s.total);
                if (bottom < min) min = bottom;
            });
            return min;
        },
        gridLines() {
            const lines = [];
            for (let i = 0; i <= 4; i++) {
                lines.push({ y: this.PAD_T + (this.H - this.PAD_T - this.PAD_B) * (i / 4) });
            }
            return lines;
        },
        // 기준일이 구간 뒤쪽이면 '오늘 기준(역산)', 앞쪽이면 '시작일 기준(순방향)'으로 본다
        baseMode() {
            if (!this.baseDate || !this.dates.length) return 'start';
            const mid = this.dates[Math.floor(this.dates.length / 2)];
            return this.baseDate > mid ? 'today' : 'start';
        },

        // 시작 시점 대비 현금 변화 (얼마나 불어났는지)
        cashEndVsStart() {
            if (!this.hasSetting || !this.series.length) return null;
            return this.latest.cash - this.num(this.setting.baseCashAmount);
        },

        // 차트 경로 — 218일치를 매번 다시 만들면 마우스만 움직여도 버벅인다.
        // computed 로 두면 데이터가 바뀔 때만 계산한다
        areaStock() { return this.areaPath('stock'); },
        areaReceivable() { return this.areaPath('receivable'); },
        areaCash() { return this.areaPath('cash'); },
        lineTotal() { return this.linePath('total'); },
        // 구간 경계선 — 현금 윗선, 현금+받을돈 윗선
        lineCash() { return this.stackLine(s => s.cash); },
        lineCashPlusRecv() { return this.stackLine(s => s.cash + s.receivable); },

        // 툴팁은 마우스가 오른쪽 끝에 가면 왼쪽으로 뒤집는다
        tipStyle() {
            if (this.hover === null || !this.series.length) return {};
            const pct = this.series.length > 1 ? (this.hover / (this.series.length - 1)) * 100 : 50;
            return pct > 60
                ? { right: (100 - pct) + '%', marginRight: '12px' }
                : { left: pct + '%', marginLeft: '12px' };
        },
        xTicks() {
            const n = this.series.length;
            if (!n) return [];
            const step = Math.max(1, Math.floor(n / 8));
            const ticks = [];
            for (let i = 0; i < n; i += step) {
                ticks.push({ label: this.series[i].date.slice(5), pct: (i / (n - 1 || 1)) * 100 });
            }
            return ticks;
        }
    },
    watch: {
        setting: {
            immediate: true,
            handler(v) {
                if (v && v.baseCashDate) {
                    this.baseDate = v.baseCashDate;
                    this.baseAmount = this.num(v.baseCashAmount);
                }
            }
        },
        // 처음 열면 데이터가 시작하는 날을 기본 시작일로 제안한다.
        // 오늘로 두면 과거가 거꾸로 계산돼서 '얼마에서 시작해 얼마가 됐다'가 안 보인다
        dates: {
            immediate: true,
            handler(list) {
                if (!this.hasSetting && !this.baseDate && list && list.length) {
                    this.baseDate = list[0];
                }
            }
        }
    },
    methods: {
        // 그 입금일에 들어올 재고손실보상 금액(확정된 것). 없으면 0
        compensationOn(date) {
            const map = this.recon && this.recon.compensationPendingByDate;
            return map ? this.num(map[date]) : 0;
        },
        num(v) {
            const n = Number(v);
            return isNaN(n) ? 0 : n;
        },
        fmt(v) {
            return Math.round(this.num(v)).toLocaleString();
        },
        // 오늘 기준 며칠 뒤인지 (요일과 함께)
        dayLabelOf(date) {
            const d = new Date(date + 'T00:00:00');
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const diff = Math.round((d - today) / 86400000);
            const week = ['일', '월', '화', '수', '목', '금', '토'][d.getDay()];
            if (diff <= 0) return `(${week}) 오늘`;
            return `(${week}) ${diff}일 뒤`;
        },
        xOf(i) {
            const n = this.series.length;
            if (n <= 1) return this.PAD_L;
            return this.PAD_L + (this.W - this.PAD_L - this.PAD_R) * (i / (n - 1));
        },
        yOf(v) {
            const span = this.maxValue - this.minValue || 1;
            const inner = this.H - this.PAD_T - this.PAD_B;
            return this.PAD_T + inner * (1 - (v - this.minValue) / span);
        },
        // 좌표 계산에 쓸 값을 숫자로만 한 번 뽑아둔다.
        //
        // 점마다 this.xOf() / this.yOf() 를 부르면 그 안에서 series·maxValue·minValue
        // computed를 매번 다시 읽는데, Vue는 computed를 읽을 때마다 그 의존성 목록을
        // 통째로 훑는다. series는 1년치 재고·입고·정산을 다 건드려 의존성이 수천 개라
        // 영역 3개 + 선 3개 × 365일이면 그 훑기가 수천 번 반복돼 2초 넘게 잡아먹었다.
        // 그래서 경로를 만들기 전에 필요한 숫자만 지역 변수로 꺼내 쓴다.
        pathGeometry() {
            const series = this.series;
            const n = series.length;
            const span = this.maxValue - this.minValue || 1;
            const inner = this.H - this.PAD_T - this.PAD_B;
            const padL = this.PAD_L;
            const padT = this.PAD_T;
            const min = this.minValue;
            const stepX = n > 1 ? (this.W - padL - this.PAD_R) / (n - 1) : 0;
            return {
                series,
                x: i => padL + stepX * i,
                y: v => padT + inner * (1 - (v - min) / span)
            };
        },
        // 스택 영역: 아래에서부터 stock → receivable → cash
        // 아래부터 현금 → 받을 돈 → 재고 순으로 쌓는다
        areaPath(kind) {
            const { series, x, y } = this.pathGeometry();
            if (!series.length) return '';
            const lower = [];
            const upper = [];
            series.forEach((s, i) => {
                let base = 0;
                let top = 0;
                if (kind === 'cash') { base = 0; top = s.cash; }
                else if (kind === 'receivable') { base = s.cash; top = s.cash + s.receivable; }
                else { base = s.cash + s.receivable; top = s.total; }
                const px = x(i);
                lower.push([px, y(base)]);
                upper.push([px, y(top)]);
            });
            const up = upper.map((p, i) => (i === 0 ? 'M' : 'L') + p[0] + ',' + p[1]).join(' ');
            const down = lower.reverse().map(p => 'L' + p[0] + ',' + p[1]).join(' ');
            return up + ' ' + down + ' Z';
        },
        linePath(field) {
            const { series, x, y } = this.pathGeometry();
            if (!series.length) return '';
            return series
                .map((s, i) => (i === 0 ? 'M' : 'L') + x(i) + ',' + y(s[field]))
                .join(' ');
        },
        // 누적 구간의 경계선 (계산식을 넘겨받는다)
        stackLine(pick) {
            const { series, x, y } = this.pathGeometry();
            if (!series.length) return '';
            return series
                .map((s, i) => (i === 0 ? 'M' : 'L') + x(i) + ',' + y(pick(s)))
                .join(' ');
        },
        // SVG는 viewBox로 늘어나 있으므로 화면 좌표를 비율로 환산해 인덱스를 찾는다.
        // mousemove는 초당 수십 번 오므로 프레임당 한 번만 반영한다
        onMove(evt) {
            const n = this.series.length;
            if (!n) return;
            const box = evt.currentTarget.getBoundingClientRect();
            if (!box.width) return;
            const x = evt.clientX;
            if (this.moveRaf) return;
            this.moveRaf = requestAnimationFrame(() => {
                this.moveRaf = null;
                const ratio = (x - box.left) / box.width;
                const idx = Math.round(ratio * (n - 1));
                const next = Math.max(0, Math.min(n - 1, idx));
                if (next !== this.hover) this.hover = next;
            });
        },
        // 날짜만 그쪽으로 옮긴다. 금액은 사용자가 그 시점 잔고로 다시 넣어야 한다
        setBaseMode(mode) {
            if (!this.dates.length) return;
            this.baseDate = mode === 'today'
                ? this.dates[this.dates.length - 1]
                : this.dates[0];
        },
        emitSetting() {
            if (!this.baseDate) {
                alert('기준일을 선택해 주세요.');
                return;
            }
            this.$emit('save-setting', {
                baseCashDate: this.baseDate,
                baseCashAmount: this.num(this.baseAmount)
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.asset-trend {
    &__head {
        display: flex;
        align-items: center;
        padding: 10px 14px;
        border-bottom: 1px solid #eef1f4;
        flex-wrap: wrap;
    }
    &__body {
        padding: 14px;
    }
    &__kpi {
        display: flex;
        flex-direction: column;
        span { color: #7f8c9a; font-size: 13px; }
        strong { font-size: 20px; }

        &--pop {
            position: relative;
            cursor: help;
            &:hover .asset-trend__schedule { display: block; }
        }

        &--link {
            cursor: pointer;
            strong { text-decoration: underline dotted; }
        }
    }
    &__kpi-recon {
        color: #c0392b;
    }
    &__schedule {
        display: none;
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        margin-top: 6px;
        width: 260px;
        background: #fff;
        border: 1px solid #cfd8e0;
        border-radius: 6px;
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.14);
        padding: 10px 12px;
        text-align: left;
        z-index: 10;
    }
    &__schedule-head {
        font-weight: 700;
        font-size: 12px;
        color: #2c3e50;
        padding-bottom: 5px;
        margin-bottom: 4px;
        border-bottom: 1px solid #eef1f4;
    }
    &__schedule-row {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        padding: 3px 0;
        font-size: 12px;
        color: #5a6b7b;
        b { color: #2c3e50; font-variant-numeric: tabular-nums; font-weight: 600; }
        &.is-total {
            margin-top: 4px;
            padding-top: 5px;
            border-top: 1px solid #eef1f4;
            font-weight: 700;
            color: #2b6cb0;
            b { color: #2b6cb0; }
        }
    }
    &__diag {
        border-radius: 6px;
        padding: 12px 14px;
        margin-bottom: 14px;
        border-left: 4px solid #cfd8e0;
        background: #f7f9fb;

        &.is-danger { background: #fdf3f2; border-left-color: #c0392b; }
        &.is-warn   { background: #fff8ec; border-left-color: #e67e22; }
        &.is-info   { background: #f0f7ff; border-left-color: #2b6cb0; }
        &.is-ok     { background: #f1faf4; border-left-color: #1e874b; }
    }
    &__diag-title {
        font-size: 15px;
        font-weight: 700;
        margin-bottom: 4px;
    }
    &__diag-action {
        font-size: 13px;
        color: #40505f;
        margin-bottom: 3px;
    }
    &__diag-why {
        font-size: 12px;
        color: #7f8c9a;
    }
    &__diag-metrics {
        display: flex;
        gap: 18px;
        margin-top: 9px;
        padding-top: 9px;
        border-top: 1px solid rgba(0, 0, 0, 0.06);
        font-size: 12px;
        color: #5a6b7b;
        strong { color: #2c3e50; }
    }
    &__chart {
        position: relative;
    }
    &__tip {
        position: absolute;
        top: 14px;
        background: #fff;
        border: 1px solid #cfd8e0;
        border-radius: 6px;
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
        padding: 9px 12px;
        font-size: 12px;
        min-width: 180px;
        pointer-events: none;
        z-index: 5;
    }
    &__tip-date {
        font-weight: 700;
        color: #2c3e50;
        margin-bottom: 6px;
        padding-bottom: 5px;
        border-bottom: 1px solid #eef1f4;
    }
    &__tip-row {
        display: flex;
        justify-content: space-between;
        gap: 16px;
        padding: 2px 0;
        color: #5a6b7b;
        b { color: #2c3e50; font-variant-numeric: tabular-nums; }
        .dot {
            display: inline-block;
            width: 9px;
            height: 9px;
            border-radius: 2px;
            margin-right: 5px;
        }
        &.is-total {
            margin-top: 5px;
            padding-top: 5px;
            border-top: 1px solid #eef1f4;
            font-weight: 700;
            color: #2b6cb0;
            b { color: #2b6cb0; }
        }
    }
    &__svg {
        width: 100%;
        height: 320px;
        display: block;
    }
    &__axis {
        position: relative;
        height: 18px;
        span {
            position: absolute;
            transform: translateX(-50%);
            font-size: 11px;
            color: #97a3ae;
            white-space: nowrap;
        }
    }
    &__legend {
        display: flex;
        gap: 18px;
        justify-content: center;
        margin-top: 6px;
        font-size: 12px;
        color: #5a6b7b;
        .dot {
            display: inline-block;
            width: 10px;
            height: 10px;
            border-radius: 2px;
            margin-right: 5px;
            &.line { height: 3px; border-radius: 0; }
        }
    }
    &__note {
        margin-top: 14px;
        background: #f8fbff;
        border: 1px solid #dbe8f5;
        border-left: 3px solid #2b6cb0;
        padding: 10px 12px;
        border-radius: 0 4px 4px 0;
        font-size: 12px;
        line-height: 1.8;
        color: #5a6b7b;
    }
}
</style>
